import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { TOTAl_ORDER_RGBA } from 'features/supplier/utils';

import BarChart from '@components/charts/BarChart';
import DoughnutChart from '@components/charts/Doughnut';
import CustomDropdown from '@components/CustomDropdown';
import DateRangeComponent from '@components/DateRangeComponent';
import ModalContainer from '@components/modals/ModalContainer';
import NoData from '@components/NoData';

import EditButton from '@features/supplier/component/EditButton';
import EditRelation from '@features/supplier/component/EditRelation';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { getSuppliersDetails } from '@store/reducers';

import { fetchSupplierData, onSubmitOfferQuantity } from '@services/supplierService';

import {
	capitalizeFirstLetter,
	changeDateFormat,
	getAllMonthsList,
	getNumberUnit,
	replaceUnderLine,
	validateNumber
} from '@utils/Util';

import CommonImage from '@/components/CommonImage';
import CustomTooltip from '@/components/CustomTooltip';

const getYearNames = () => {
	const currentYear = new Date().getFullYear();
	const startYear = 2019;
	const yearNames = [];
	for (let year = startYear; year <= currentYear; year++) {
		yearNames.push(year.toString());
	}
	return yearNames;
};

const getInitialEndDate = () => {
	const today = new Date();
	const twoYearsAgo = new Date(today.getFullYear() - 2, today.getMonth(), 1);
	const startDate = new Date(twoYearsAgo.getFullYear(), twoYearsAgo.getMonth(), 1);
	return startDate.toISOString().split('T')[0];
};

const Relation = () => {
	const [isMomEdit, setIsMomEdit] = useState(false);
	const [isRelationEdit, setIsRelationEdit] = useState(false);
	const [relationData, setRelationData] = useState(null);
	const [chartData, setChartData] = useState(null);
	const [startDate, setStartDate] = useState(getInitialEndDate());
	const [endDate, setEndSate] = useState(new Date().toISOString().split('T')[0]);
	const [monthlyOffer, setMonthlyOffer] = useState(null);
	const [year, setYear] = useState(new Date().getFullYear());
	const [, setOrderHoverdData] = useState([]);
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isLoading = useSelector((store) => store.loaderStore);
	const supplierDetails = useSelector(getSuppliersDetails);

	useEffect(() => {
		onSetRelationData();
	}, [startDate, endDate]);

	useEffect(() => {
		onSetChartData();
	}, [year, startDate, endDate]);

	// useEffect(() => {
	// 	if (startDate && endDate) {
	// 		onSetChartData();
	// 	}
	// }, [startDate, endDate]);

	useEffect(() => {
		setOrderHoverdData(
			chartData?.productCategoryWiseOrderList?.map(
				() => `#${Math.floor(Math.random() * 16777215).toString(16)}`
			)
		);
	}, [chartData?.productCategoryWiseOrderList]);

	const onSetRelationData = async () => {
		try {
			dispatch(openLoader());
			const responseData = await fetchSupplierData({
				type: 'RELATION',
				supplierId: id,
				startDate,
				endDate
			});
			setRelationData(responseData);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onSetChartData = async () => {
		try {
			dispatch(openLoader());
			const responseData = await fetchSupplierData({ type: 'CHART', supplierId: id, year });
			if (startDate && endDate) {
				const response = await fetchSupplierData({
					type: 'CHART',
					supplierId: id,
					startDate,
					endDate
				});
				responseData.brandWiseOrderList = response.brandWiseOrderList;
				responseData.deliveryWisePOList = response.deliveryWisePOList;
				responseData.productCategoryWiseOrderList = response.productCategoryWiseOrderList;
				responseData.categoryWiseCostingList = response.categoryWiseCostingList;
				responseData.marginAmount = response.marginAmount;
				responseData.marginPercentage = response.marginPercentage;
				responseData.styleShippedCount = response.styleShippedCount;
				responseData.onTimePercentage = response.onTimePercentage;
			}
			setChartData(responseData);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	const onMomEditClick = async () => {
		try {
			dispatch(openLoader());
			const responseData = await fetchSupplierData({
				type: 'MONTHLY_OFFER_QUANTITY',
				supplierId: id,
				year
			});
			if (responseData) {
				setMonthlyOffer(responseData);
				setIsMomEdit(true);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const onChangeDateRange = async (value1, value2) => {
		setStartDate(value1);
		setEndSate(value2);
	};

	const dataFields = [
		{
			label: 'Garments',
			value: relationData?.garmentsSampleDevelopmentQty || '--',
			value2: relationData?.garmentsTargetQty ? '/' + relationData?.garmentsTargetQty + ' pcs' : '--'
		},
		{
			label: 'Fabric',
			value: relationData?.fabricSampleDevelopmentQty || '--',
			value2: relationData?.fabricTargetQty ? '/' + relationData?.fabricTargetQty + ' kg' : '--'
		},
		{
			label: 'Avg sample lead time',
			value: relationData?.avgSampleLeadTime ? relationData?.avgSampleLeadTime + ' Days' : '--'
		},
		{
			label: 'No. of order',
			value: relationData?.numberOfTotalOrder ? relationData?.numberOfTotalOrder : '--'
		},
		{
			label: 'Completed order',
			value: relationData?.numberOfCompletedOrder ? relationData?.numberOfCompletedOrder : '--'
		},
		{
			label: 'Running order',
			value: relationData?.numberOfRunningOrder ? relationData?.numberOfRunningOrder : '--'
		},
		{
			label: 'Order value',
			value: relationData?.orderValue ? '$' + getNumberUnit(relationData?.orderValue) : '--'
		},
		{ label: 'Quantity', value: relationData?.totalQty ? relationData?.totalQty : '--' },
		{ label: 'No. of style', value: relationData?.numberOfStyle ? relationData?.numberOfStyle : '--' },
		{ label: 'No. of PO', value: relationData?.numberOfPO ? relationData?.numberOfPO : '--' },
		{
			label: 'Avg. production lead time',
			value: relationData?.avgProductionLeadTime ? relationData?.avgProductionLeadTime + ' Days' : '--'
		},
		{
			icon: '/icons/info.svg',
			tooltipText: 'Based on shipment data from July 2025',
			label: 'On-time percentage',
			value: chartData?.onTimePercentage ? chartData?.onTimePercentage.toFixed(2) + '%' : '--'
		},
		{
			label: 'Margin amount',
			value: chartData?.marginAmount ? '$' + getNumberUnit(chartData?.marginAmount) : '--'
		},
		{
			label: 'Margin percentage',
			value: chartData?.marginPercentage ? chartData?.marginPercentage.toFixed(2) + '%' : '--'
		},
		{
			label: 'Style shipped count',
			value: chartData?.styleShippedCount ? chartData?.styleShippedCount.toFixed(2) : '--'
		}
	];
	const renderDataFields = () => {
		return dataFields.map((field, index) => (
			<div key={index} className="single same-padding-box">
				<div className="d-flex items-center justify-content-between">
					<p className="regular-14 gray_dark_02">{field.label}</p>
					{field?.icon && (
						<div>
							<CustomTooltip text={field?.tooltipText} arrowCenter={true}>
								<CommonImage src={field?.icon} alt="info" />
							</CustomTooltip>
						</div>
					)}
				</div>
				<h3 className="title regular-18 gray_dark_01 weight-500">
					{field.value}
					{['Garments', 'Fabric']?.includes(field?.label) && field.value !== '--' && (
						<span className="regular-14 gray_dark_02 font-weight-normal">{field?.value2}</span>
					)}
				</h3>
			</div>
		));
	};

	const labels = chartData?.capacityOfferQtyOrderList?.map((item) =>
		changeDateFormat(item?.date, '', 'MMM, YY')
	);

	const categoryWiseCostingLabels = chartData?.categoryWiseCostingList?.map((item) => item.productCategory);

	const orderCapacityOfferData = [
		{
			label: 'Capacity',
			data: chartData?.capacityOfferQtyOrderList?.map((item) => item?.capacity),
			backgroundColor: 'rgba(0, 149, 255, 1)',
			// barThickness: 15,
			borderRadius: 5,
			barPercentage: 0.8,
			categoryPercentage: 0.35
		},
		{
			label: 'Offer Qty',
			data: chartData?.capacityOfferQtyOrderList?.map((item) => item?.offerQty),
			backgroundColor: 'rgba(0, 224, 150, 1)',
			// barThickness: 15,
			borderRadius: 5,
			barPercentage: 0.8,
			categoryPercentage: 0.35
		},
		{
			label: 'Order Placement',
			data: chartData?.capacityOfferQtyOrderList?.map((item) => item?.numberOfOrderQty),
			backgroundColor: 'rgba(244, 121, 32, 1)',
			// barThickness: 15,
			borderRadius: 5,
			barPercentage: 0.8,
			categoryPercentage: 0.35
		}
	];

	const CategoryWiseChartData = [
		{
			label: 'Maximum Price',
			data: chartData?.categoryWiseCostingList?.map((item) => item?.maximumPrice),
			backgroundColor: 'rgba(0, 149, 255, 1)',
			// barThickness: 15,
			borderRadius: 5,
			barPercentage: 0.8,
			categoryPercentage: 0.35
		},
		{
			label: 'Average Price',
			data: chartData?.categoryWiseCostingList?.map((item) => item?.averagePrice),
			backgroundColor: 'rgba(0, 224, 150, 1)',
			// barThickness: 15,
			borderRadius: 5,
			barPercentage: 0.8,
			categoryPercentage: 0.35
		}
	];

	const brandLabels = chartData?.brandWiseOrderList?.map((item) => item?.brandName);
	const brandData = [
		{
			label: 'Brand',
			data: chartData?.brandWiseOrderList.map((item) => item?.totalAmount),
			backgroundColor: 'rgba(59, 130, 246, 1)',
			borderRadius: 5,
			barPercentage: 0.7,
			categoryPercentage: 0.3
		}
	];

	const colorCodes = chartData?.productCategoryWiseOrderList?.map((item) => TOTAl_ORDER_RGBA[item.id - 1]);
	const orderChart = {
		labels: chartData?.productCategoryWiseOrderList?.map((item) => item?.productCategory),
		datasets: [
			{
				data: chartData?.productCategoryWiseOrderList?.map((item) => item?.totalNumber),
				backgroundColor: colorCodes,
				hoverBackgroundColor: colorCodes
			}
		]
	};
	const DELIVERY_PO_COLOR = {
		ON_TIME: '#67AC5B',
		DELAY: '#FCB400',
		CANCELLED: '#F46748'
	};
	const poChartData = {
		labels: chartData?.deliveryWisePOList?.map((item) => replaceUnderLine(item?.stageName)),
		datasets: [
			{
				data: chartData?.deliveryWisePOList?.map((item) => item?.totalNumber),
				backgroundColor: ['#67AC5B', '#FCB400', '#F46748'],
				hoverBackgroundColor: ['#67AC5B', '#FCB400', '#F46748']
			}
		]
	};
	const handleChange = (idx, e) => {
		const updatedMonthlyOfferQtyResponseList = monthlyOffer?.monthlyOfferQtyResponseList?.map((item) => {
			if (parseInt(item.month) === parseInt(idx + 1)) {
				const qty = e.target.value.trim() !== '' ? e.target.value : null;
				return { ...item, qty };
			}
			return item;
		});
		setMonthlyOffer({
			...monthlyOffer,
			monthlyOfferQtyResponseList: updatedMonthlyOfferQtyResponseList
		});
	};

	const onSubmitMomOfferQty = async () => {
		try {
			dispatch(openLoader());
			await onSubmitOfferQuantity({
				supplierId: id,
				year: monthlyOffer?.year,
				body: monthlyOffer?.monthlyOfferQtyResponseList
			});
			onSetChartData();
			dispatch(closeLoader());
			setIsMomEdit(false);
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderMomOffer = () => {
		return (
			<ModalContainer>
				<div className="common-popup monthly-offer-modal">
					<div className="common-popup-header d-flex justify-content-between">
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">Monthly booking qty</h3>
						</div>
						<div className="close-btn" onClick={() => setIsMomEdit(false)}>
							<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
						</div>
					</div>
					<div className="common-popup-body">
						<h3 className="regular-14 gray_dark_02 mb-3">
							Monthly booking qty for {monthlyOffer?.year}
						</h3>
						<table className="table with-border height-sm hover">
							<thead>
								<tr>
									<th className="weight-500">Month name</th>
									<th className="weight-500">Booking Qty</th>
								</tr>
							</thead>
							<tbody>
								{monthlyOffer?.monthlyOfferQtyResponseList?.map((item, idx) => (
									<tr key={item?.month}>
										<td>{getAllMonthsList[item?.month - 1]}</td>
										<td>
											<input
												type="number"
												value={item?.qty || ''}
												onKeyPress={validateNumber}
												onChange={(e) => handleChange(idx, e)}
											/>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="modal-footer">
						<div className="footer-buttons text-right">
							<button
								className="button size32 ml-2"
								disabled={isLoading}
								onClick={onSubmitMomOfferQty}
							>
								Submit
							</button>
						</div>
					</div>
				</div>
			</ModalContainer>
		);
	};

	const redirectToCostingList = () => {
		navigate(`/costing/initial?supplierId=${id}&supplierName=${supplierDetails.name}`);
	};

	return (
		<div>
			<div className="capacity-section relation-with-nitex-section section-padding hover-section">
				<div className="capacity-header d-flex justify-content-between">
					<div>
						<h3 className="suplier-title pb-4 font-weight-bold regular-16">
							Relation with Nitex
							<EditButton onEditClick={() => setIsRelationEdit(true)} />
						</h3>
					</div>
					<div>
						<DateRangeComponent
							displayFormat="MMM DD,YYYY"
							item={{ value1: startDate, value2: endDate }}
							onChange={onChangeDateRange}
							showClearDates={true}
						/>
					</div>
				</div>
				<div className="capacity-items">{renderDataFields()}</div>
			</div>

			<div className="order-chart-top">
				<div className="chart-header mb-4 d-flex justify-content-between align-items-center">
					<div>
						<h3 className="suplier-title text-capitalize mb-0 font-weight-bold regular-16">
							Category Wise Costing ({chartData?.categoryWiseCostingList?.length})
							<EditButton onEditClick={() => onMomEditClick(true)} />
						</h3>
					</div>
					{chartData?.categoryWiseCostingList?.length > 0 && (
						<div className="chart-status d-flex justify-content-between align-items-center">
							<div className="perfomence-list">
								<ul className="d-flex align-items-center">
									<li className="sm-fonts regular-12">
										<span className="rd-bg bg-capecity" />
										Maximum Price
									</li>
									<li className="sm-fonts regular-12">
										<span className="rd-bg bg-Qty" />
										Average Price
									</li>
									<li className="sm-fonts regular-12">|</li>
									<li className="sm-fonts regular-12" onClick={redirectToCostingList}>
										<span className="regular-16 gray_dark_01 cursor-pointer">
											Costing List
										</span>{' '}
									</li>
								</ul>
							</div>
						</div>
					)}
				</div>
				{chartData?.categoryWiseCostingList?.length > 0 ? (
					<div>
						<BarChart
							chartData={CategoryWiseChartData}
							chartLabels={categoryWiseCostingLabels}
							optional={{
								scales: {
									x: {
										grid: {
											display: false
										}
									},
									y: {
										ticks: {
											callback: function (value) {
												return getNumberUnit(value);
											}
										}
									}
								},
								plugins: {
									tooltip: {
										callbacks: {
											title: (tooltipItem) => tooltipItem[0]?.dataset?.label,
											label: function (tooltipItem) {
												return `$${getNumberUnit(tooltipItem.raw)}`;
											}
										}
									}
								}
							}}
						/>
					</div>
				) : (
					<NoData message={'No Data found'} />
				)}
			</div>

			{/* order-chart */}
			<div className="order-section section-padding hover-section">
				{chartData?.productCategoryWiseOrderList?.length > 0 && (
					<div className="order-chart-middle">
						<div className="order-chart-box">
							<h3 className="suplier-title pb-4 font-weight-bold regular-16 text-capitalize">
								Category wise order quantity
							</h3>
							<div className="d-flex align-items-center">
								<div className="chart-category">
									{chartData?.productCategoryWiseOrderList?.slice(0, 5)?.map((item) => (
										<div className="chart-list d-flex align-items-center" key={item?.id}>
											<div className="mr-1">
												<span
													className="dots"
													style={{
														backgroundColor: TOTAl_ORDER_RGBA[item?.id - 1]
													}}
												></span>
											</div>

											<span className="cate-name regular-14 gray_dark_02 weight-500">
												{capitalizeFirstLetter(item?.productCategory)}:{' '}
											</span>
											{item?.percentage > 0 && (
												<span className="regular-14 gray_dark_01 weight-500">
													{parseFloat(item?.percentage * 100).toFixed(1)}%
												</span>
											)}
										</div>
									))}
								</div>
								<div className="chart-view">
									<DoughnutChart chartData={orderChart} />
								</div>
							</div>
						</div>
						<div className="order-chart-box">
							<h3 className="suplier-title pb-4 font-weight-bold regular-16 text-capitalize">
								On-Time Delivery Ratio
							</h3>
							<div className="d-flex align-items-center">
								<div className="chart-category">
									{chartData?.deliveryWisePOList?.slice(0, 5)?.map((item) => (
										<div className="chart-list d-flex align-items-center" key={item?.id}>
											{item?.percentage > 0 && (
												<>
													<div className="mr-1">
														<span
															className="dots"
															style={{
																backgroundColor:
																	DELIVERY_PO_COLOR[item?.stageName]
															}}
														></span>
													</div>

													<span className="cate-name regular-14 gray_dark_02 weight-500">
														{replaceUnderLine(item?.stageName)}:{' '}
													</span>

													<span className="regular-14 gray_dark_01 weight-500">
														{parseFloat(item?.percentage * 100).toFixed(1)}%
													</span>
												</>
											)}
										</div>
									))}
								</div>
								<div className="m-auto text-center chart-view">
									<DoughnutChart chartData={poChartData} />
								</div>
							</div>
						</div>
					</div>
				)}

				{chartData?.brandWiseOrderList?.length > 0 && (
					<div className="order-progress-bottom section-padding">
						<h3 className="suplier-title pb-4 font-weight-bold regular-16 text-capitalize">
							Brand wise order value
						</h3>
						<div className>
							<BarChart
								chartData={brandData}
								chartLabels={brandLabels}
								optional={{
									indexAxis: 'y',
									barPercentage: 1,
									scales: {
										x: {
											grid: {
												display: false
											},
											ticks: {
												callback: function (value) {
													return '$' + getNumberUnit(value);
												}
											}
										},
										y: {
											grid: {
												display: false
											}
										}
									},
									plugins: {
										tooltip: {
											callbacks: {
												title: () => '',
												label: function (tooltipItem) {
													return `$${getNumberUnit(tooltipItem.raw)}`;
												}
											}
										}
									}
								}}
							/>
						</div>
					</div>
				)}

				<div className="order-chart-top">
					<div className="chart-header mb-4 d-flex justify-content-between align-items-center">
						<div>
							<h3 className="suplier-title text-capitalize mb-0 font-weight-bold regular-16">
								Capacity Vs Booking Qty Vs Order Placement{' '}
								<EditButton onEditClick={() => onMomEditClick(true)} />
							</h3>
						</div>
						{chartData?.capacityOfferQtyOrderList?.length > 0 && (
							<div className="chart-status d-flex justify-content-between align-items-center">
								<div className="perfomence-list">
									<ul className="d-flex">
										<li className="sm-fonts regular-12">
											<span className="rd-bg bg-capecity" />
											Capacity
										</li>
										<li className="sm-fonts regular-12">
											<span className="rd-bg bg-Qty" />
											Booking Qty
										</li>
										<li className="sm-fonts regular-12">
											<span className="rd-bg bg-order" />
											Order Placement
										</li>
									</ul>
								</div>

								<div className="year-status transparent-select align-items-center">
									<div className="form-group d-flex align-items-center mb-0">
										<label className="mr-2" htmlFor="collection">
											Year
										</label>
										<CustomDropdown
											type="addItem"
											title={'Select Brand'}
											onItemClick={(item) => setYear(item?.value)}
											items={getYearNames()?.map((item) => ({
												key: item,
												value: item
											}))}
											selectedItem={year}
											isDisabled={false}
										/>
									</div>
								</div>
							</div>
						)}
					</div>
					{chartData?.capacityOfferQtyOrderList?.length > 0 ? (
						<div>
							<BarChart
								chartData={orderCapacityOfferData}
								chartLabels={labels}
								optional={{
									scales: {
										x: {
											grid: {
												display: false
											}
										},
										y: {
											ticks: {
												callback: function (value) {
													return getNumberUnit(value);
												}
											}
										}
									},
									plugins: {
										tooltip: {
											callbacks: {
												title: (tooltipItem) => tooltipItem[0]?.dataset?.label,
												label: function (tooltipItem) {
													return `$${getNumberUnit(tooltipItem.raw)}`;
												}
											}
										}
									}
								}}
							/>
						</div>
					) : (
						<NoData message={'No Data found'} />
					)}
				</div>
			</div>
			{isRelationEdit && (
				<EditRelation
					onClose={() => {
						onSetRelationData();
						setIsRelationEdit(false);
					}}
				/>
			)}
			{isMomEdit && renderMomOffer()}
		</div>
	);
};

export default Relation;
